'use client';

import { useEffect, useState } from 'react';

/** A navigation that never lands (cancelled, failed) must not leave the mark spinning. */
const GIVE_UP_MS = 10_000;

/**
 * True from a tap on a link to another page until that page's pathname arrives. Read off the
 * document rather than each Link, so every link on the page counts, the sheet's and the cards'.
 * Next's own `useLinkStatus` reports only for the one Link it sits inside.
 */
export function useNavigating(pathname: string): boolean {
  // The page the tap left from; the mark spins only while we are still standing on it.
  const [leaving, setLeaving] = useState<string | null>(null);

  useEffect(() => {
    let giveUp = 0;
    const onClick = (event: MouseEvent) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const link = (event.target as Element | null)?.closest?.('a[href]');
      if (!(link instanceof HTMLAnchorElement) || link.target === '_blank' || link.download) return;
      const url = new URL(link.href, window.location.href);
      // Another site, or this page again (a reselect, a query or hash change): no page to wait for.
      if (url.origin !== window.location.origin || url.pathname === pathname) return;
      setLeaving(pathname);
      window.clearTimeout(giveUp);
      giveUp = window.setTimeout(() => setLeaving(null), GIVE_UP_MS);
    };
    document.addEventListener('click', onClick);
    // Arriving clears it too, so a later Back to the page left does not find it still marked.
    return () => {
      document.removeEventListener('click', onClick);
      window.clearTimeout(giveUp);
      setLeaving(null);
    };
  }, [pathname]);

  return leaving === pathname;
}
